import React, { useState, useMemo, useRef } from 'react';
import styles from './BookingSlotPicker.module.css';

const BookingSlotPicker = ({
    appointments,  // Object from API: { "1": { initial_time: "...", final_time: "..." }, ... }
    onSlotSelect   // function(date, time) 
}) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [viewStartDate, setViewStartDate] = useState(new Date()); // Controls the 14-day window start

    // Refs for drag-to-scroll
    const dateInputRef = useRef(null);
    const sliderRef = useRef(null);
    const isDown = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const hasDragged = useRef(false); // New ref to track movement

    // --- 1. Generate Next 14 Days ---
    const dates = useMemo(() => {
        const list = [];
        // Start from viewStartDate instead of fixed "today"
        const start = new Date(viewStartDate);

        for (let i = 0; i < 14; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            list.push(d);
        }
        return list;
    }, [viewStartDate]);

    // Handle date picker change
    const handleDatePick = (e) => {
        if (!e.target.value) return;

        // Parse "YYYY-MM-DD" from input to local Date
        const [y, m, d] = e.target.value.split('-').map(Number);
        const newDate = new Date(y, m - 1, d);

        setViewStartDate(newDate);
        setSelectedDate(newDate);
    };

    // Trigger hidden input on button click
    const openDatePicker = () => {
        dateInputRef.current?.showPicker();
    };

    // --- Drag-to-Scroll Handlers ---
    const handleMouseDown = (e) => {
        isDown.current = true;
        hasDragged.current = false; // Reset drag flag
        sliderRef.current.classList.add(styles.active);
        startX.current = e.pageX - sliderRef.current.offsetLeft;
        scrollLeft.current = sliderRef.current.scrollLeft;
    };

    const handleMouseLeave = () => {
        isDown.current = false;
        sliderRef.current?.classList.remove(styles.active);
    };

    const handleMouseUp = () => {
        isDown.current = false;
        sliderRef.current?.classList.remove(styles.active);
        // Note: hasDragged remains true if we moved, but resets on next mousedown
        setTimeout(() => { hasDragged.current = false; }, 0); // Reset after click phase
    };

    const handleMouseMove = (e) => {
        if (!isDown.current) return;
        e.preventDefault();
        const x = e.pageX - sliderRef.current.offsetLeft;
        const walk = (x - startX.current) * 2; // Scroll-fast

        // Only consider it a drag if moved more than threshold
        if (Math.abs(walk) > 5) {
            hasDragged.current = true;
        }

        sliderRef.current.scrollLeft = scrollLeft.current - walk;
    };

    // Wrapper for Date Selection
    const handleDateClick = (date) => {
        if (hasDragged.current) return; // Ignore if user was dragging
        setSelectedDate(date);
    };

    // Wrapper for Picker Open
    const handlePickerClick = () => {
        if (hasDragged.current) return;
        openDatePicker();
    };

    // --- Wheel Scroll Handler ---
    const handleWheel = (e) => {
        if (sliderRef.current) {
            // Translate vertical scroll to horizontal scroll
            // Only if deltaY is present (vertical scroll)
            if (e.deltaY !== 0) {
                // Prevent default only if we are actually scrolling horizontally to avoid blocking page scroll entirely if unrelated
                // But for this container, we want to capture it.
                // React synthetic event might not allow preventDefault on wheel easily if passive.
                // We'll just scroll.
                sliderRef.current.scrollLeft += e.deltaY;
            }
        }
    };

    // --- 2. Generate 24h Slots (Events are 30 min) ---
    const timeSlots = useMemo(() => {
        const slots = [];
        // 00:00 to 23:30
        for (let h = 0; h < 24; h++) {
            for (let m = 0; m < 60; m += 30) {
                const hourStr = h.toString().padStart(2, '0');
                const minStr = m.toString().padStart(2, '0');
                slots.push(`${hourStr}:${minStr}`);
            }
        }
        return slots;
    }, []);

    // --- 3. Filter Logic (Collision Detection) ---
    const isSlotBusy = (timeStr) => {
        if (!appointments) return false;

        // Construct the specific datetime for this slot
        const [h, m] = timeStr.split(':').map(Number);
        const slotStart = new Date(selectedDate);
        slotStart.setHours(h, m, 0, 0);

        const slotEnd = new Date(slotStart);
        slotEnd.setMinutes(slotEnd.getMinutes() + 30); // Assuming 30 min service for simplified check

        // Flatten appointments object to array
        const busyTimes = Object.values(appointments);

        return busyTimes.some(appt => {
            const busyStart = new Date(appt.initial_time);
            const busyEnd = new Date(appt.final_time);

            // Collision formula: (StartA < EndB) and (EndA > StartB)
            return slotStart < busyEnd && slotEnd > busyStart;
        });
    };

    // Also disable past times if today
    const isPast = (timeStr) => {
        const now = new Date();
        // If selected date is future, never past
        if (selectedDate.setHours(0, 0, 0, 0) > now.setHours(0, 0, 0, 0)) return false;

        // If selected date is today, check time
        const [h, m] = timeStr.split(':').map(Number);
        const slotTime = new Date(selectedDate);
        slotTime.setHours(h, m, 0, 0);

        return slotTime < new Date();
    };

    return (
        <div className={styles.container}>
            {/* Date Strip */}
            <div
                className={styles.dateStrip}
                ref={sliderRef}
                onMouseDown={handleMouseDown}
                onMouseLeave={handleMouseLeave}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onWheel={handleWheel}
            >
                {dates.map((date) => {
                    const isSelected = date.toDateString() === selectedDate.toDateString();
                    return (
                        <div
                            key={date.toISOString()}
                            className={`${styles.dateCard} ${isSelected ? styles.active : ''}`}
                            onClick={() => handleDateClick(date)}
                        >
                            <span className={styles.dayName}>
                                {date.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')}
                            </span>
                            <span className={styles.dayNumber}>
                                {date.getDate()}
                            </span>
                        </div>
                    );
                })}

                {/* 0. Date Picker Trigger */}
                <div
                    className={`${styles.dateCard} ${styles.pickerCard}`}
                    onClick={handlePickerClick}
                    title="Escolher outra data"
                >
                    {/* Hidden Input */}
                    <input
                        ref={dateInputRef}
                        type="date"
                        className={styles.hiddenInput}
                        onChange={handleDatePick}
                    />
                    <span className={styles.dayName}>Outra</span>
                    <span className={styles.dayNumber}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                    </span>
                </div>
            </div>

            {/* Time Grid */}
            <div className={styles.timeGrid}>
                {timeSlots.map((time) => {
                    const busy = isSlotBusy(time);
                    const past = isPast(time);
                    const disabled = busy || past;

                    return (
                        <button
                            key={time}
                            className={styles.timeChip}
                            disabled={disabled}
                            onClick={() => !disabled && onSlotSelect(selectedDate, time)}
                        >
                            {time}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default BookingSlotPicker;
